import React, { useState, useEffect } from 'react';
import MediumCard from './MediumCard';
import { getCurrentMediumsStatus } from '../data/mediums';
import { Medium } from '../types';
import { 
    LockIcon, EyeIcon, ShieldIcon, CheckCircleIcon, 
    PhoneIcon, StarIcon, ChevronDownIcon, HeartIcon,
    MoonIcon, InfoIcon, BriefcaseIcon, SparklesIcon, MessageCircleIcon
} from './Icons';
import Hero from './Hero';

interface HomeProps {
    onNavigate: (page: string, category?: string | null) => void;
    onChatStart: (medium: Medium) => void;
    onProfileClick: (medium: Medium) => void;
    onOpenPremium: (initialPack?: '50' | '100' | '150') => void;
    onOpenProfile?: () => void;
    guestUser?: { name: string; phone: string } | null;
}

export const Home: React.FC<HomeProps> = ({ 
    onNavigate, 
    onChatStart, 
    onProfileClick, 
    onOpenPremium, 
    onOpenProfile, 
    guestUser 
}) => {
    // Récupération dynamique des médiums selon l'heure
    const [liveMediums, setLiveMediums] = useState<Medium[]>([]);
    
    // Fonction pour charger et mélanger une sélection aléatoire de médiums connectés à chaque chargement
    const loadLiveMediums = () => {
        const meds = getCurrentMediumsStatus()
            .filter(m => m.status === 'available' || m.status === 'busy');
        // Mélange aléatoire (Fisher-Yates shuffle) pour charger une liste dynamique à chaque fois
        const shuffled = [...meds].sort(() => Math.random() - 0.5);
        setLiveMediums(shuffled);
    };

    useEffect(() => {
        loadLiveMediums();
    }, []);

    const displayMediums = liveMediums.slice(0, 12);
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const toggleFaq = (index: number) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    return (
        <div className="bg-transparent text-gray-100 min-h-screen pb-20 animate-fade-in">
            
            {/* === MESSAGE DE BIENVENUE UTILISATEUR === */}
            {guestUser && (
                <div className="container mx-auto px-4 pt-6 pb-2 text-center animate-fade-in">
                    <div className="inline-block bg-gradient-to-r from-purple-900/50 to-indigo-900/50 backdrop-blur-md border border-purple-500/30 px-6 py-2 rounded-full">
                        <p className="text-purple-200 text-sm font-medium">
                            Ravi de vous revoir, <strong className="text-gold-400">{guestUser.name}</strong>. Nos experts vous attendent.
                        </p>
                    </div>
                </div>
            )}

            {/* === HERO SECTION AVEC LES 5 BOUTONS INTÉGRÉS === */}
            <Hero onNavigate={onNavigate} onOpenPremium={() => onNavigate('tarifs')} onOpenProfile={onOpenProfile} />

            {/* === SECTION AUDIOTEL DIRECT HAUTE ÉLÉGANCE (AVANT LES FORFAITS) === */}
            <section className="py-8 bg-gradient-to-r from-[#0d071a] via-[#140a2b] to-[#0d071a] border-t border-purple-900/50 relative overflow-hidden">
                <div className="container mx-auto px-4 max-w-5xl">
                    <div className="bg-gradient-to-r from-[#170B2F] via-[#1F0E3D] to-[#170B2F] border-2 border-gold-500/50 hover:border-gold-400 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8 shadow-[0_0_40px_rgba(251,191,36,0.15)] relative overflow-hidden group transition-all duration-300">
                        
                        {/* Effet reflet doré subtil */}
                        <div className="absolute top-0 left-[-100%] w-[50%] h-full bg-gradient-to-r from-transparent via-gold-400/10 to-transparent skew-x-12 group-hover:left-[200%] transition-all duration-1000 ease-in-out pointer-events-none"></div>

                        {/* Bloc Texte & Présentation */}
                        <div className="flex-1 text-center md:text-left z-10">
                            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gold-500 via-amber-400 to-gold-500 text-black font-black text-[10px] sm:text-xs px-3.5 py-1 rounded-full mb-3 uppercase tracking-widest shadow-md">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                <span>Ligne Directe 24h/24 • Sans Carte Bancaire</span>
                            </div>

                            <h3 className="text-2xl sm:text-3xl font-serif font-black text-white mb-2 tracking-tight">
                                Voyance Téléphonique Immédiate
                            </h3>

                            <p className="text-purple-200/90 text-xs sm:text-sm mb-4 leading-relaxed max-w-xl">
                                Entrez en communication vocale directe avec nos médiums purs. Vos prédictions et guidances spirituelles vous sont révélées en direct, sans attente et en toute confidentialité.
                            </p>

                            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2.5 sm:gap-3 text-xs">
                                <span className="flex items-center gap-1.5 bg-black/50 border border-purple-800/60 px-3 py-1.5 rounded-xl text-gray-200">
                                    <LockIcon className="w-3.5 h-3.5 text-gold-400 shrink-0"/> 
                                    <span>100% Anonyme</span>
                                </span>
                                <span className="flex items-center gap-1.5 bg-black/50 border border-purple-800/60 px-3 py-1.5 rounded-xl text-gray-200">
                                    <SparklesIcon className="w-3.5 h-3.5 text-emerald-400 shrink-0"/> 
                                    <span>Sans Inscription</span>
                                </span>
                                <span className="flex items-center gap-1.5 bg-gold-500/15 border border-gold-500/40 px-3 py-1.5 rounded-xl text-gold-300 font-bold">
                                    <span>0,80 € / min (Débit opérateur)</span>
                                </span>
                            </div>
                        </div>

                        {/* Bloc Bouton d'Appel Gold & Noir */}
                        <div className="flex flex-col items-center gap-2.5 z-10 shrink-0 w-full md:w-auto">
                            <div className="text-center">
                                <span className="text-[11px] uppercase font-bold text-gold-400 tracking-widest block">Numéro d'Appel Direct</span>
                                <span className="text-3xl sm:text-4xl font-black text-white font-mono tracking-tight block">09 85 40 52 74</span>
                            </div>
                            
                            <a 
                                href="tel:0985405274"
                                className="w-full sm:w-auto bg-gradient-to-r from-gold-500 via-amber-400 to-gold-500 hover:from-gold-400 hover:to-amber-300 text-black font-black py-3.5 px-8 rounded-full shadow-[0_0_30px_rgba(251,191,36,0.35)] hover:shadow-[0_0_40px_rgba(251,191,36,0.6)] transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-wider cursor-pointer border border-gold-300/60"
                            >
                                <PhoneIcon className="w-4 h-4 text-black animate-bounce" />
                                <span>Appeler Maintenant</span>
                            </a>
                            <span className="text-[10px] text-gray-400">0,80€ / min + prix d'un appel</span>
                        </div>

                    </div>
                </div>
            </section>

             {/* === PACKS VOYANCE & FORFAITS DETAIL === */}
             <section className="py-10 bg-gradient-to-r from-gold-900/20 to-black/50 border-y border-gold-500/20">
                <div className="container mx-auto px-4">
                    <div className="max-w-5xl mx-auto bg-gradient-to-r from-black via-mystic-900 to-black border-2 border-gold-500/50 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-8 shadow-[0_0_35px_rgba(251,191,36,0.2)] relative overflow-hidden group">
                        
                        {/* Effet brillant */}
                        <div className="absolute top-0 left-[-100%] w-[50%] h-full bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 group-hover:left-[200%] transition-all duration-1000 ease-in-out"></div>

                        <div className="flex-1 text-center md:text-left z-10">
                            <div className="inline-block bg-gold-500 text-black font-bold text-xs px-3 py-1 rounded-full mb-3 uppercase tracking-widest animate-pulse">
                                Forfaits Voyance Privée
                            </div>
                            <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-2">
                                Recharges Transcash : <span className="text-gold-400">50€, 100€ & 150€</span>
                            </h3>
                            <p className="text-gray-300 text-sm mb-4">
                                Profitez jusqu'à <strong className="text-white">90 minutes</strong> de consultation privée en ligne sans aucun surcoût.
                            </p>
                            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-xs text-gray-300">
                                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4 text-gold-500"/> Pack Sérénité 50€ (27 min)</span>
                                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4 text-amber-400"/> Pack Privilège 100€ (57 min)</span>
                                <span className="flex items-center gap-1"><CheckCircleIcon className="w-4 h-4 text-cyan-400"/> Pack Prestige 150€ (90 min)</span>
                            </div>
                        </div>

                        <div className="flex flex-col items-center gap-2 z-10 shrink-0">
                            <div className="text-center">
                                <span className="text-xs uppercase font-bold text-gray-400 block">À partir de</span>
                                <span className="text-4xl md:text-5xl font-bold text-white">50€</span>
                            </div>
                            <button 
                                onClick={() => onNavigate('tarifs')}
                                className="bg-gradient-to-r from-gold-600 to-yellow-500 hover:from-gold-500 hover:to-yellow-400 text-black font-extrabold py-3 px-7 rounded-full shadow-lg transition-transform hover:scale-105 flex items-center gap-2 text-xs uppercase tracking-wider cursor-pointer"
                            >
                                <StarIcon className="w-4 h-4 fill-black" /> Commander mon Pack
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* === MÉDIUMS CONNECTÉS EN DIRECT === */}
            <section className="py-12 md:py-16 container mx-auto px-4 max-w-7xl">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 border-b border-mystic-700/60 pb-4 gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 text-gold-400 text-xs font-bold uppercase tracking-wider mb-1">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                            Cabinet Ouvert 24h/24
                        </div>
                        <h3 className="text-2xl md:text-3xl font-serif font-bold text-white">
                            Nos Médiums en Direct
                        </h3>
                    </div>
                    <button 
                        onClick={() => onNavigate('mediums')}
                        className="text-xs md:text-sm font-bold text-gold-400 hover:text-gold-300 flex items-center gap-1 transition-colors cursor-pointer"
                    >
                        <span>Voir les {liveMediums.length} experts connectés</span>
                        <span>→</span>
                    </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                    {displayMediums.map((medium) => (
                        <MediumCard 
                            key={medium.id} 
                            medium={medium} 
                            onChatStart={onChatStart}
                            onProfileClick={onProfileClick}
                        />
                    ))}
                </div>
            </section>

            {/* === FAQ ACCUEIL === */}
            <section className="py-12 bg-black/40 border-t border-purple-900/30">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h3 className="text-2xl font-serif font-bold text-center text-white mb-8">
                        Questions Fréquentes
                    </h3>
                    
                    <div className="space-y-3">
                        {[
                            { q: "Comment se déroule une consultation sur Astrox.fr ?", a: "Vous pouvez échanger par chat en direct ou appel vocal direct avec nos médiums. Le chat est confidentiel et vous recevez des réponses précises et personnalisées." },
                            { q: "Les paiements par Transcash sont-ils sécurisés et anonymes ?", a: "Oui, nous acceptons les coupons Transcash. Aucun libellé bancaire lié à la voyance n'apparaît." },
                            { q: "Que sont les Astres et comment les utiliser ?", a: "Les Astres sont vos crédits de consultation. 1€ = 10 Astres. Les Astres achetés n'expirent jamais." }
                        ].map((faq, idx) => (
                            <div key={idx} className="rounded-2xl bg-[#12102A]/80 border border-purple-900/40 overflow-hidden">
                                <button
                                    onClick={() => toggleFaq(idx)}
                                    className="w-full text-left p-4 text-xs sm:text-sm font-bold text-white flex items-center justify-between gap-3 cursor-pointer"
                                >
                                    <span>{faq.q}</span>
                                    <span className={`text-purple-400 transition-transform ${openFaq === idx ? 'rotate-180 text-amber-300' : ''}`}>▼</span>
                                </button>
                                {openFaq === idx && (
                                    <div className="p-4 pt-0 text-xs text-purple-200/80 leading-relaxed border-t border-purple-900/20">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default Home;
